/**
 * What the account's working sheet shows (Encre, mock-up « Fiche » — Gilles, 12/10/2026: the side
 * sheet is the working sheet, the full record stays one click away): the deals in progress, the
 * people to reach, the latest exchanges in one timeline. Read from the data the sheet already loads
 * (`useAccountDetailData`): nothing is re-derived that a reader already says. Pure, but for the amounts it
 * words: they pass through the presentation mode, fixed when the page loads.
 */
import type { CRMContact, CRMOpportunity } from '../../types/crm';
import type { CRMNote } from '../../types/notes';
import { sensitive } from '../presentationMode';

/** The deals still open, the most recently moved first. */
export function openOpportunities(opportunities: readonly CRMOpportunity[]): CRMOpportunity[] {
  return opportunities
    .filter((o) => o.status === 'open')
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

/** The people to reach first: those with a way to reach them, then the others, at most `max`. */
export function keyContacts<C extends Pick<CRMContact, 'phone' | 'email'>>(contacts: readonly C[], max = 4): C[] {
  const reachable = (c: C) => Boolean(c.phone || c.email);
  return [...contacts.filter(reachable), ...contacts.filter((c) => !reachable(c))].slice(0, max);
}

/** An appointment as the sheet reads it (the rows of `appointments`, joined to their contact). */
export interface SheetAppointment {
  id: string;
  start_time: string;
  title?: string | null;
  status?: string | null;
  contact?: { first_name: string | null; last_name: string | null } | null;
}

/** A meeting report as the sheet reads it (`crm_meeting_reports`). */
export interface SheetReport {
  id: string;
  cr_title: string | null;
  created_at: string;
}

export interface Exchange {
  kind: 'appointment' | 'note' | 'report';
  id: string;
  at: string;
  label: string;
  /** Who it was with, when the row says. */
  who: string | null;
}

const personName = (p: { first_name: string | null; last_name: string | null } | null | undefined): string | null => {
  const name = [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim();
  return name || null;
};

/** Lines that carry no words: a rule, a code fence, a table's separator row. */
const MARKUP_ONLY = /^\s*(?:[-*_](?:\s*[-*_]){2,}|`{3}.*|~{3}.*|\|?\s*:?-{2,}:?\s*(?:\|\s*:?-{2,}:?\s*)*\|?)\s*$/u;

/** A Markdown line as a person reads it: its markers go, its words stay (« 12 * 3 » keeps its star). */
const plainLine = (raw: string): string =>
  raw
    // A heading, quotes (even nested), a bullet with or without its task box, a number.
    .replace(/^\s*(?:#{1,6}\s+|(?:>\s*)+|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+)/u, '')
    // A link or an image says its text.
    .replace(/!?\[([^\]]*)\]\([^)]*\)/gu, '$1')
    .replace(/~~|`+/gu, '')
    // `*` and `_` mark emphasis only against a word's edge: « jean_dupont », « 12 * 3 » keep theirs.
    .replace(/(^|[^\p{L}\p{N}*])\*{1,3}(?=[\p{L}\p{N}])|(?<=[\p{L}\p{N}])\*{1,3}(?![\p{L}\p{N}*])/gu, '$1')
    .replace(/(^|[^\p{L}\p{N}_])_{1,3}(?=[\p{L}\p{N}])|(?<=[\p{L}\p{N}])_{1,3}(?![\p{L}\p{N}_])/gu, '$1')
    // A table row says its cells.
    .replace(/^\s*\||\|\s*$/gu, '')
    .replace(/\s*\|\s*/gu, ' · ')
    .trim();

const clipWords = (line: string, max: number): string => (line.length > max ? `${line.slice(0, max).replace(/\s+\S*$/u, '')}…` : line);

/** A note's lines that carry words, as text (notes are Markdown). */
const textLines = (text: string): string[] =>
  text
    .split(/\r?\n/u)
    .filter((l) => !MARKUP_ONLY.test(l))
    .map(plainLine)
    .filter(Boolean);

/** A note's first line as a person reads it. */
const firstLine = (text: string, max = 90): string => clipWords(textLines(text)[0] ?? '', max);

/** A note's text as a preview reads it: its lines without Markdown, one after the other (« · »). */
export function notePreview(text: string, max = 240): string {
  return clipWords(textLines(text).join(' · '), max);
}

/**
 * The latest exchanges in one timeline, the newest first: appointments (a cancelled one did not
 * happen), notes and, when given, meeting reports.
 */
export function exchangesOf(
  appointments: readonly SheetAppointment[],
  notes: readonly CRMNote[],
  { reports = [], max = 6 }: { reports?: readonly SheetReport[]; max?: number } = {},
): Exchange[] {
  const fromAppointments: Exchange[] = appointments.filter((a) => a.status !== 'cancelled').map((a) => ({
    kind: 'appointment',
    id: a.id,
    at: a.start_time,
    label: a.title?.trim() || 'Rendez-vous',
    who: personName(a.contact),
  }));
  const fromNotes: Exchange[] = notes.map((n) => ({
    kind: 'note',
    id: n.id,
    at: n.created_at,
    label: n.title?.trim() || firstLine(n.content) || 'Note',
    who: personName(n.contact ?? null),
  }));
  const fromReports: Exchange[] = reports.map((r) => ({
    kind: 'report',
    id: r.id,
    at: r.created_at,
    label: r.cr_title?.trim() || 'Compte rendu',
    who: null,
  }));
  return [...fromAppointments, ...fromNotes, ...fromReports].sort((a, b) => b.at.localeCompare(a.at)).slice(0, max);
}

const DAY = 86_400_000;

/** « Aujourd'hui », « Hier », « Demain », else « 5 oct. » (« 5 oct. 2025 » another year). */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(date) - startOf(now)) / DAY);
  if (days === 0) return "Aujourd'hui";
  if (days === -1) return 'Hier';
  if (days === 1) return 'Demain';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** « 12 500 € », or null when the deal has no amount. */
export function euros(amount: number | null): string | null {
  if (amount === null || Number.isNaN(amount)) return null;
  return sensitive(`${Math.round(amount).toLocaleString('fr-FR')} €`);
}

